import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  PlayIcon,
  CalendarDaysIcon,
  CloudIcon,
  ChartBarIcon,
  CpuChipIcon,
} from '@heroicons/react/24/outline'
import { getLocalApiUrl, authFetch } from '../../config/api'
import {
  formatDateTime,
  formatTradingDay,
  statusBadge,
  formatBytes,
  buildGamingImportResultMessage,
  ImportResultBanner,
  ImportErrorCount,
  parseImportErrorDetails,
  downloadDailyDataExtractFile,
} from './dailyDataUtils'
import DailyDataNav from './DailyDataNav'
import BatchIngestProgressModal from './BatchIngestProgressModal'
import MeterReconciliationPanel from './MeterReconciliationPanel'
import ImportConfirmModal from './ImportConfirmModal'
import BlobFilePreviewModal from './BlobFilePreviewModal'
import ImportErrorsModal from './ImportErrorsModal'

const FEEDS = {
  egm: {
    title: 'EGM registry',
    description: 'Import DimMachine extracts into egm_registry (denom, zone, status).',
    extractLabel: 'DimMachine',
    icon: CpuChipIcon,
    enabledKey: 'egm_enabled',
    showDenomSync: true,
  },
  sessions: {
    title: 'Gaming sessions',
    description: 'Import FactPlayerSession extracts into gaming_sessions.',
    extractLabel: 'FactPlayerSession',
    icon: ChartBarIcon,
    enabledKey: 'sessions_enabled',
  },
  meter: {
    title: 'Meter readings',
    description: 'Import Meter extracts into egm_meter_readings.',
    extractLabel: 'Meter',
    icon: CpuChipIcon,
    enabledKey: 'meter_enabled',
  },
  'slot-analysis': {
    title: 'Slot analysis',
    description: 'Import FactSlotAnalysisDaily extracts into fact_slot_analysis_daily (rated turnover, jackpot, session counts).',
    extractLabel: 'FactSlotAnalysisDaily',
    icon: ChartBarIcon,
    enabledKey: 'slot_analysis_enabled',
  },
  tito: {
    title: 'TITO Data',
    description: 'Import TITO voucher files from the same extract folder. Pick the System 7000 or Ebet template so the columns match that system.',
    extractLabel: 'TITO-',
    icon: ChartBarIcon,
    enabledKey: 'tito_enabled',
  },
}

export default function AzureExtractFeed({ feedId }) {
  const feed = FEEDS[feedId]
  if (!feed) return null

  const apiBase = `/api/daily-data/${feedId}`
  const Icon = feed.icon

  const [data, setData] = useState(null)
  const [azureSources, setAzureSources] = useState(null)
  const [loading, setLoading] = useState(true)
  const [loadingAzure, setLoadingAzure] = useState(false)
  const [running, setRunning] = useState(false)
  const [message, setMessage] = useState(null)
  const [tradingDay, setTradingDay] = useState('')
  const [templateId, setTemplateId] = useState('')
  const [clearBeforeImport, setClearBeforeImport] = useState(false)
  const [expandedFolder, setExpandedFolder] = useState(null)
  const [azureNav, setAzureNav] = useState({ year: null, month: null })
  const [batchIngest, setBatchIngest] = useState(null)
  const [confirmOpts, setConfirmOpts] = useState(null)
  const [previewBlobPath, setPreviewBlobPath] = useState(null)
  const [downloadingBlobPath, setDownloadingBlobPath] = useState(null)
  const [errorModal, setErrorModal] = useState(null)
  const [errorModalLoading, setErrorModalLoading] = useState(false)
  const resultRef = useRef(null)

  const openImportErrors = useCallback(async (ctx) => {
    const initial = {
      title: ctx.title || `${feed.title} import errors`,
      tradingDay: ctx.tradingDay,
      runAt: ctx.runAt,
      errorCount: ctx.errorCount ?? 0,
      imported: ctx.imported,
      updated: ctx.updated,
      skipped: ctx.skipped,
      filePath: ctx.filePath,
      errorDetails: parseImportErrorDetails(ctx),
    }
    setErrorModal(initial)
    if (!ctx.runId) return
    setErrorModalLoading(true)
    try {
      const res = await authFetch(getLocalApiUrl(`${apiBase}/runs/${ctx.runId}`))
      const json = await res.json()
      if (json.success && json.data) {
        const d = json.data
        setErrorModal({
          ...initial,
          errorDetails: parseImportErrorDetails(d),
          imported: d.imported ?? initial.imported,
          updated: d.updated ?? initial.updated,
          skipped: d.skipped ?? initial.skipped,
          filePath: d.file_path || initial.filePath,
          runAt: d.run_at || initial.runAt,
          tradingDay: d.trading_day ? String(d.trading_day).slice(0, 10) : initial.tradingDay,
          errorCount: d.errors ?? initial.errorCount,
        })
      }
    } catch {
      /* keep initial details */
    } finally {
      setErrorModalLoading(false)
    }
  }, [apiBase, feed.title])

  const downloadFile = async (blobPath, fileName) => {
    if (!blobPath || downloadingBlobPath) return
    setDownloadingBlobPath(blobPath)
    try {
      await downloadDailyDataExtractFile(feedId, blobPath, fileName || 'extract.txt')
    } catch (e) {
      showResult({
        type: 'error',
        summary: `Download failed — ${e.message || 'unknown error'}`,
        errorDetails: [],
      })
    } finally {
      setDownloadingBlobPath(null)
    }
  }

  const showResult = useCallback((result, extra = {}) => {
    setMessage({ ...result, ...extra })
    requestAnimationFrame(() => {
      resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    })
  }, [])

  const fetchAzureSources = async ({ year, month } = {}) => {
    const params = new URLSearchParams()
    if (year) params.set('year', year)
    if (month) params.set('month', month)
    const qs = params.toString()
    const res = await authFetch(getLocalApiUrl(`${apiBase}/sources${qs ? `?${qs}` : ''}`))
    const json = await res.json()
    if (!json.success) throw new Error(json.error || 'Failed to list Azure extracts')
    return json.data
  }

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await authFetch(getLocalApiUrl(`${apiBase}?days=14`))
      const json = await res.json()
      if (!json.success) throw new Error(json.error || 'Failed to load feed')
      setData(json.data)
      setTradingDay(prev => prev || json.data?.trading_day_yesterday || json.data?.trading_day_today || '')
      setTemplateId(prev => prev || (json.data?.default_template_id ? String(json.data.default_template_id) : ''))
      return true
    } catch (e) {
      setMessage({ type: 'error', text: e.message })
      return false
    } finally {
      setLoading(false)
    }
  }, [apiBase])

  const loadAzure = useCallback(async (nav = {}) => {
    setLoadingAzure(true)
    try {
      let year = nav.year ?? null
      let month = nav.month ?? null
      if (nav.level === 'root') { year = null; month = null }
      else if (nav.level === 'year') month = null

      if (!nav.explicit && !year) {
        const root = await fetchAzureSources({})
        if (root.years?.length) year = root.years[0].year
      }
      if (!nav.explicit && year && !month) {
        const monthsView = await fetchAzureSources({ year })
        if (monthsView.months?.length) month = monthsView.months[0].month_key
      }

      const blobData = year && month
        ? await fetchAzureSources({ year, month })
        : year
          ? await fetchAzureSources({ year })
          : await fetchAzureSources({})

      setAzureSources(blobData)
      setAzureNav({ year: blobData.year || year || null, month: blobData.month || month || null })
    } catch (e) {
      setMessage({ type: 'error', text: e.message })
    } finally {
      setLoadingAzure(false)
    }
  }, [apiBase])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    if (data?.config?.source_type === 'azure_blob' && !azureSources) loadAzure()
  }, [data, azureSources, loadAzure])

  const requestImport = (opts = {}) => {
    const day = opts.tradingDay ?? tradingDay
    if (!opts.blobPath && !day && opts.tradingDay !== 'yesterday') {
      setMessage({ type: 'error', summary: 'Select a trading day or pick a file from Azure' })
      return
    }
    setConfirmOpts(opts)
  }

  const handleImportConfirm = (payload = {}) => {
    const venueMappings = payload?.venueMappings || null
    const clearBeforeImportFromModal = payload?.clearBeforeImport
    if (confirmOpts?.importAllBatches) {
      const folder = azureDayFolders.find(
        s => s.folder_path === confirmOpts.folderPath
          || s.trading_day === confirmOpts.tradingDay,
      )
      if (folder?.files?.length) {
        setBatchIngest({
          files: folder.files,
          tradingDay: folder.trading_day,
          folderPath: folder.folder_path,
          folderName: folder.folder_name,
          venueMappings,
        })
        setConfirmOpts(null)
        return
      }
    }
    runImport(confirmOpts, venueMappings, clearBeforeImportFromModal)
  }

  const runImport = async (opts = {}, venueMappings = null, clearBeforeImportFromModal = null) => {
    setRunning(true)
    setMessage(null)
    try {
      const body = {}
      if (opts.tradingDay) body.trading_day = opts.tradingDay
      if (opts.blobPath) body.blob_path = opts.blobPath
      if (templateId) body.template_id = parseInt(templateId, 10)
      if (opts.importAllBatches) {
        body.import_all_batches = true
        if (opts.folderPath) body.folder_path = opts.folderPath
      }
      if (venueMappings && Object.keys(venueMappings).length) {
        body.venue_mappings = venueMappings
      }
      // Add clear_before_import for meter imports
      // Use modal value if provided, otherwise use component state (from trading day form)
      const shouldClear = clearBeforeImportFromModal !== null ? clearBeforeImportFromModal : clearBeforeImport
      if ((feedId === 'meter' || feedId === 'egm') && shouldClear) {
        body.clear_before_import = true
      }
      const res = await authFetch(getLocalApiUrl(`${apiBase}/run`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      const json = await res.json()
      if (!json.success) throw new Error(json.error || 'Import failed')
      const d = json.data
      const result = buildGamingImportResultMessage(d, {
        feedLabel: feed.title,
        tradingDay: opts.tradingDay ?? tradingDay,
        showDenomSync: feed.showDenomSync,
      })
      const hint = feedId === 'meter' && d.trading_day
        ? <>See <Link to={`/monitor-safe/daily-data/turnover?day=${d.trading_day}`} className="underline font-medium">Daily turnover</Link> for totals and reconciliation.</>
        : null
      if (d.trading_day) setTradingDay(d.trading_day)
      showResult(result, { hint })
      setConfirmOpts(null)
      await load()
      if (data?.config?.source_type === 'azure_blob') await loadAzure({ explicit: true, year: azureNav.year, month: azureNav.month })
    } catch (e) {
      showResult({
        type: 'error',
        summary: `${feed.title} failed — ${e.message}`,
        errorDetails: [],
      })
    } finally {
      setRunning(false)
    }
  }

  const handleBatchComplete = async (totals) => {
    const result = buildGamingImportResultMessage(null, {
      feedLabel: feed.title,
      batch: totals,
      tradingDay: batchIngest?.tradingDay ?? tradingDay,
    })
    const hint = feedId === 'meter'
      ? <>See <Link to={`/monitor-safe/daily-data/turnover?day=${batchIngest?.tradingDay || tradingDay}`} className="underline font-medium">Daily turnover</Link> for totals and reconciliation.</>
      : null
    if (batchIngest?.tradingDay) setTradingDay(batchIngest.tradingDay)
    setBatchIngest(null)
    showResult(result, { hint })
    await load()
    if (data?.config?.source_type === 'azure_blob') await loadAzure({ explicit: true, year: azureNav.year, month: azureNav.month })
  }

  const isAzure = data?.config?.source_type === 'azure_blob'

  // Header Refresh: re-read feed status AND the Azure file list, with visible feedback. Never starts an import.
  const handleRefresh = async () => {
    if (loading || loadingAzure) return
    const ok = await load()
    if (ok && isAzure) await loadAzure({ explicit: true, year: azureNav.year, month: azureNav.month })
    if (ok) toast.success('Refreshed')
    else toast.error('Refresh failed')
  }
  const cfg = data?.config
  const enabled = cfg?.[feed.enabledKey] !== false
  const azureDayFolders = azureSources?.days?.length ? azureSources.days : (azureSources?.sources || [])
  const importBusy = running || !!batchIngest || !!confirmOpts

  const navigateAzure = (level, year = null, month = null) => {
    setExpandedFolder(null)
    loadAzure({ explicit: true, level, year, month })
  }

  const startBatchIngest = (folder) => {
    if (!folder?.files?.length) return
    requestImport({
      tradingDay: folder.trading_day,
      folderPath: folder.folder_path,
      importAllBatches: true,
    })
  }

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <DailyDataNav />

      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="rounded-lg bg-primary-50 p-2 border border-primary-100">
              <Icon className="w-7 h-7 text-primary-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{feed.title}</h1>
              <p className="text-gray-600 mt-1">{feed.description}</p>
              <p className="text-xs text-gray-500 mt-2">
                Source: <span className="font-medium">{cfg?.source_type || 'local'}</span>
                {isAzure && (
                  <>
                    {' '}
                    —{' '}
                    <span className="font-mono">
                      {azureSources?.account_name || 'Azure'}/
                      {cfg?.azure_blob_container || azureSources?.container || 'tsg'}/
                      {cfg?.azure_blob_prefix || azureSources?.prefix || 'TPDataExtracts/'}
                    </span>
                  </>
                )}
                {!enabled && <span className="ml-2 text-amber-700">(disabled in import config)</span>}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleRefresh}
            disabled={loading || loadingAzure}
            className="inline-flex items-center px-3 py-2 border rounded-md text-sm hover:bg-gray-50 disabled:opacity-50"
          >
            <ArrowPathIcon className={`w-4 h-4 mr-1 ${loading || loadingAzure ? 'animate-spin' : ''}`} />
            {loading || loadingAzure ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      {!enabled && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          {feed.title} import is disabled. Enable it in{' '}
          <Link to="/monitor-safe/daily-data/settings" className="underline font-medium">Daily Data settings</Link>.
        </div>
      )}

      {(running || batchIngest) && (
        <div className="rounded-lg border-2 border-sky-300 bg-sky-50 p-4 text-sm text-sky-900 flex items-center gap-3">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-sky-600 shrink-0" />
          <span>{batchIngest ? `Batch ingest in progress (${batchIngest.files?.length || 0} files)…` : `Importing ${feed.title.toLowerCase()}…`}</span>
        </div>
      )}

      {(isAzure || azureSources) && (
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <CloudIcon className="w-5 h-5 text-sky-600" />
              Available in Azure (TPDataExtracts)
            </h2>
            <button type="button" onClick={() => loadAzure({ explicit: true, year: azureNav.year, month: azureNav.month })} disabled={loadingAzure} className="text-sm text-primary-600 hover:underline disabled:opacity-50">
              {loadingAzure ? 'Loading…' : 'Refresh list'}
            </button>
          </div>

          {azureSources && !azureSources.configured && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 mb-4">
              {azureSources.error || 'Azure blob storage is not configured for this tenant.'}
              {' '}Check Daily Data settings (container/prefix) and Group Configuration → venue integrations for SID storage connection string.
            </div>
          )}

          {azureSources?.configured && (
            <>
              <nav className="flex flex-wrap items-center gap-1 text-sm text-gray-600 mb-4">
                <button type="button" onClick={() => navigateAzure('root')} className="font-mono text-primary-600 hover:underline">TPDataExtracts</button>
                {azureNav.year && (
                  <>
                    <span>/</span>
                    <button type="button" onClick={() => navigateAzure('year', azureNav.year)} className="font-mono text-primary-600 hover:underline">{azureNav.year}</button>
                  </>
                )}
                {azureNav.year && azureNav.month && (
                  <>
                    <span>/</span>
                    <span className="font-mono text-gray-900">{azureNav.month}</span>
                    {azureSources.month_label && (
                      <span className="text-gray-500 ml-1">({azureSources.month_label})</span>
                    )}
                  </>
                )}
              </nav>

              {azureSources.level === 'years' && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {(azureSources.years || []).map(y => (
                    <button
                      key={y.year}
                      type="button"
                      onClick={() => navigateAzure('year', y.year)}
                      className="px-4 py-3 border rounded-lg hover:bg-gray-50 text-left"
                    >
                      <div className="text-lg font-semibold text-gray-900">{y.year}</div>
                      <div className="text-xs text-gray-500 font-mono truncate">{y.path}</div>
                    </button>
                  ))}
                  {!azureSources.years?.length && (
                    <p className="col-span-full text-sm text-gray-500 py-4 text-center">No year folders under {azureSources.prefix}</p>
                  )}
                </div>
              )}

              {azureSources.level === 'months' && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {(azureSources.months || []).map(m => (
                    <button
                      key={m.month_key}
                      type="button"
                      onClick={() => navigateAzure('month', azureSources.year, m.month_key)}
                      className="px-4 py-3 border rounded-lg hover:bg-gray-50 text-left"
                    >
                      <div className="font-semibold text-gray-900">{m.label}</div>
                      <div className="text-xs text-gray-500 font-mono">{m.month_key}</div>
                    </button>
                  ))}
                  {!azureSources.months?.length && (
                    <p className="col-span-full text-sm text-gray-500 py-4 text-center">No month folders for {azureSources.year}</p>
                  )}
                </div>
              )}

              {azureSources.level === 'days' && (
                <>
                  <p className="text-xs text-gray-500 mb-3">
                    {azureDayFolders.length} day folder(s) · {azureDayFolders.filter(d => d.has_extract).length} with {feed.extractLabel} files
                  </p>
                  <div className="space-y-2">
                    {azureDayFolders.map(folder => (
                      <div key={folder.folder_path} className="border rounded-lg overflow-hidden">
                        <button
                          type="button"
                          onClick={() => setExpandedFolder(expandedFolder === folder.folder_path ? null : folder.folder_path)}
                          className="w-full flex flex-wrap items-center justify-between gap-2 px-4 py-3 bg-gray-50 hover:bg-gray-100 text-left"
                        >
                          <div>
                            <span className="font-medium text-gray-900">
                              {folder.trading_day ? formatTradingDay(folder.trading_day) : folder.folder_name}
                            </span>
                            {folder.folder_name && folder.trading_day && (
                              <span className="ml-2 text-sm font-mono text-gray-500">{folder.folder_name}</span>
                            )}
                            {!folder.has_extract && (
                              <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-gray-200 text-gray-600">No {feed.extractLabel}</span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <span className="text-gray-500">{folder.file_count || 0} file(s){folder.total_bytes ? ` · ${formatBytes(folder.total_bytes)}` : ''}</span>
                            {folder.has_extract && (
                              <button
                                type="button"
                                disabled={importBusy || !enabled}
                                onClick={(e) => { e.stopPropagation(); startBatchIngest(folder) }}
                                className="px-2 py-1 bg-primary-600 text-white rounded text-xs hover:bg-primary-700 disabled:opacity-50"
                              >
                                Ingest all
                              </button>
                            )}
                          </div>
                        </button>
                        {expandedFolder === folder.folder_path && folder.files?.length > 0 && (
                          <ul className="divide-y divide-gray-100">
                            {folder.files.map(file => (
                              <li key={file.blob_path} className="flex flex-wrap items-center justify-between gap-2 px-4 py-2 text-sm">
                                <span className="font-mono text-gray-800 truncate max-w-xl" title={file.blob_path}>{file.name}</span>
                                <span className="text-gray-500">{formatBytes(file.size)}</span>
                                <button
                                  type="button"
                                  onClick={() => setPreviewBlobPath(file.blob_path)}
                                  className="text-gray-600 hover:underline text-sm"
                                >
                                  Preview
                                </button>
                                <button
                                  type="button"
                                  disabled={!!downloadingBlobPath}
                                  onClick={() => downloadFile(file.blob_path, file.name)}
                                  className="text-gray-600 hover:underline text-sm disabled:opacity-50"
                                >
                                  {downloadingBlobPath === file.blob_path ? 'Downloading…' : 'Download'}
                                </button>
                                <button
                                  type="button"
                                  disabled={importBusy || !enabled}
                                  onClick={() => requestImport({ blobPath: file.blob_path, tradingDay: folder.trading_day })}
                                  className="text-primary-600 hover:underline font-medium disabled:opacity-50"
                                >
                                  Ingest
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      )}

      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Import by trading day</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <CalendarDaysIcon className="w-4 h-4 inline mr-1" />
              Trading day
            </label>
            <input type="date" value={tradingDay} onChange={(e) => setTradingDay(e.target.value)} className="w-full border rounded-md px-3 py-2 text-sm" />
          </div>
          {!feed.noTemplate && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Mapping template</label>
            <select
              value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
              className="w-full border rounded-md px-3 py-2 text-sm"
            >
              <option value="">Default template</option>
              {(data?.templates || []).map(t => (
                <option key={t.id} value={t.id}>
                  {t.template_name}{t.gaming_machine_vendor ? ` — ${t.gaming_machine_vendor}` : ''}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              <Link to="/monitor-safe/daily-data/settings" className="text-primary-600 hover:underline">Settings</Link>
              {' · '}
              <Link to="/admin/group-config" className="text-primary-600 hover:underline">Edit mappings</Link>
            </p>
          </div>
          )}
        </div>
        {(feedId === 'meter' || feedId === 'egm') && (
          <div className="mt-4">
            <label className="inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={clearBeforeImport}
                onChange={(e) => setClearBeforeImport(e.target.checked)}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 mr-2"
              />
              <span className="text-sm text-gray-700">
                {feedId === 'egm'
                  ? 'Clear existing DimMachine remap keys before import'
                  : 'Clear existing meter data before import'}
                <span className="ml-1 text-gray-500">
                  {feedId === 'egm'
                    ? '(resets vendor_machine_pk so house mapping is rewritten)'
                    : '(also clears combined/enriched/rollups for that day)'}
                </span>
              </span>
            </label>
          </div>
        )}
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button
            type="button"
            disabled={importBusy || !enabled || !tradingDay}
            onClick={() => requestImport({ tradingDay })}
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-md text-sm hover:bg-primary-700 disabled:opacity-50"
          >
            <PlayIcon className="w-4 h-4 mr-1" />
            {running ? 'Importing…' : 'Import trading day'}
          </button>
          <button
            type="button"
            disabled={importBusy || !enabled}
            onClick={() => requestImport({ tradingDay: 'yesterday' })}
            className="inline-flex items-center px-4 py-2 border rounded-md text-sm hover:bg-gray-50 disabled:opacity-50"
          >
            Import yesterday
          </button>
        </div>

        <div ref={resultRef} className="mt-4">
          {message && !running && !batchIngest && (
            <ImportResultBanner
              message={message}
              onDismiss={() => setMessage(null)}
            />
          )}
        </div>
      </div>

      {feedId === 'meter' && data?.latest_run?.file_trading_window?.within === false && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950" role="status">
          {data.latest_run.file_trading_window.message}
        </div>
      )}

      {feedId === 'meter' && (
        <div className="rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm text-indigo-900 flex flex-wrap items-center justify-between gap-2">
          <span>Need turnover for a trading day? Use the dedicated lookup — not tied to import.</span>
          <Link
            to={`/monitor-safe/daily-data/turnover${tradingDay ? `?day=${tradingDay}` : ''}`}
            className="font-medium underline hover:no-underline"
          >
            Open Daily turnover →
          </Link>
        </div>
      )}

      {feedId === 'meter' && (
        <MeterReconciliationPanel
          tradingDay={tradingDay}
          onTradingDayChange={setTradingDay}
          refreshKey={data?.last_success?.run_at || data?.latest_run?.run_at}
        />
      )}

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Recent trading days</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Day</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Imported</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Skipped</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Errors</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Azure</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(data?.trading_days || []).map(row => (
                <tr key={row.trading_day} className="hover:bg-gray-50">
                  <td className="px-4 py-2 text-sm">{formatTradingDay(row.trading_day)}</td>
                  <td className="px-4 py-2 text-sm">{statusBadge(row.status)}</td>
                  <td className="px-4 py-2 text-sm text-right">{row.imported ?? '—'}</td>
                  <td className="px-4 py-2 text-sm text-right text-gray-600">{row.skipped ?? '—'}</td>
                  <td className="px-4 py-2 text-sm text-right">
                    <ImportErrorCount
                      count={row.errors}
                      onClick={() => openImportErrors({
                        title: `${feed.title} errors — ${formatTradingDay(row.trading_day)}`,
                        runId: row.run_id,
                        tradingDay: row.trading_day,
                        runAt: row.run_at,
                        errorCount: row.errors,
                        imported: row.imported,
                        updated: row.updated,
                        skipped: row.skipped,
                        filePath: row.file_path,
                        error_message: row.error_message,
                        error_details: row.error_details,
                      })}
                    />
                  </td>
                  <td className="px-4 py-2 text-sm text-right text-gray-600">
                    {row.azure_blob_available ? `${row.azure_file_count} file(s)` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Import run log</h2>
        {(data?.recent_runs || []).length === 0 ? (
          <p className="text-sm text-gray-500 py-4 text-center">No import runs yet</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Run at</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Trading day</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Imported</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Skipped</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Errors</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.recent_runs.map(run => (
                  <tr key={run.id}>
                    <td className="px-3 py-2 whitespace-nowrap">{formatDateTime(run.run_at)}</td>
                    <td className="px-3 py-2">{run.trading_day ? formatTradingDay(String(run.trading_day).slice(0, 10)) : '—'}</td>
                    <td className="px-3 py-2">
                      {statusBadge(run.status === 'success' ? 'ingested' : run.status)}
                      {run.file_trading_window?.within === false && (
                        <div className="mt-1 text-xs font-medium text-amber-800" title={run.file_trading_window.message}>
                          Outside venue hours
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">{run.imported ?? 0}</td>
                    <td className="px-3 py-2 text-right text-gray-600">{run.skipped ?? 0}</td>
                    <td className="px-3 py-2 text-right text-red-700">
                      <ImportErrorCount
                        count={run.errors}
                        onClick={() => openImportErrors({
                          title: `${feed.title} import run errors`,
                          runId: run.id,
                          tradingDay: run.trading_day ? String(run.trading_day).slice(0, 10) : null,
                          runAt: run.run_at,
                          errorCount: run.errors,
                          imported: run.imported,
                          updated: run.updated,
                          skipped: run.skipped,
                          filePath: run.file_path,
                          error_message: run.error_message,
                          error_details: run.error_details,
                        })}
                      />
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-gray-500 max-w-xs truncate" title={run.file_path}>{run.file_path || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {batchIngest && (
        <BatchIngestProgressModal
          open
          files={batchIngest.files}
          tradingDay={batchIngest.tradingDay}
          folderName={batchIngest.folderName}
          apiPath={`${apiBase}/run`}
          folderPath={batchIngest.folderPath}
          templateId={templateId}
          venueMappings={batchIngest.venueMappings}
          onClose={() => setBatchIngest(null)}
          onComplete={handleBatchComplete}
          onViewErrors={openImportErrors}
        />
      )}

      <ImportConfirmModal
        open={!!confirmOpts}
        importOpts={confirmOpts}
        templateId={templateId}
        feedId={feedId}
        feedTitle={feed.title}
        onClose={() => { if (!running) setConfirmOpts(null) }}
        onConfirm={handleImportConfirm}
        confirming={running}
      />

      <BlobFilePreviewModal
        open={!!previewBlobPath}
        blobPath={previewBlobPath}
        feedId={feedId}
        onClose={() => setPreviewBlobPath(null)}
      />

      <ImportErrorsModal
        open={!!errorModal}
        onClose={() => { setErrorModal(null); setErrorModalLoading(false) }}
        title={errorModal?.title}
        tradingDay={errorModal?.tradingDay}
        runAt={errorModal?.runAt}
        errorCount={errorModal?.errorCount ?? 0}
        imported={errorModal?.imported}
        updated={errorModal?.updated}
        skipped={errorModal?.skipped}
        filePath={errorModal?.filePath}
        errorDetails={errorModal?.errorDetails}
        loading={errorModalLoading}
      />
    </div>
  )
}
